import React from "react";
import { Link } from "react-router-dom";
import "../css/main.scss";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { editorialImageSrcSet } from "../utils/images";
import PageMeta from "./PageMeta";
import ProductCard from "./ProductCard";

const featuredPickIds = [11, 19];

const homeCollections = [
  {
    category: "Pasta & Grains",
    categoryId: "pasta-grains",
    eyebrow: "The pasta shelf",
    title: "Shapes with a little more character",
    description:
      "Bronze-cut pasta and patient pantry grains chosen for their texture, flavor, and beautifully simple ingredient lists.",
    tone: "cream",
  },
  {
    category: "Tomatoes & Sauces",
    categoryId: "tomatoes-sauces",
    eyebrow: "Red, ripe, ready",
    title: "Tomatoes that taste like summer",
    description:
      "Whole fruit, silky passata, and slow-simmered sauces that bring bright Southern Italian flavor to the everyday table.",
    tone: "tomato",
  },
  {
    category: "Olive Oil & Vinegar",
    categoryId: "olive-oil-vinegar",
    eyebrow: "For the final drizzle",
    title: "A bottle can change the whole plate",
    description:
      "Lively oils and rounded vinegars selected to season generously, finish simply, and keep within easy reach.",
    tone: "sage",
  },
  {
    category: "Herbs & Antipasti",
    categoryId: "herbs-antipasti",
    eyebrow: "Open a jar, gather around",
    title: "Aperitivo starts in the pantry",
    description:
      "Fragrant herbs, savory seasonings, and ready-to-share antipasti for the hour when snacks become supper.",
    tone: "gold",
  },
];

function Main() {
  const picks = featuredPickIds
    .map((id) => productData.find((product) => product.id === id))
    .filter(Boolean);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="main home-page">
      <PageMeta
        title="Italian pantry, gathered with care"
        description={siteConfig.description}
      />

      <main>
        <section className="home-hero" aria-labelledby="home-hero-title">
          <div className="home-hero__copy">
            <p className="home-kicker">Italian food for real life</p>
            <h1 id="home-hero-title">Bring a little light to the table.</h1>
            <p className="home-hero__lede">
              Thoughtful staples from small Italian makers, gathered for easy
              dinners, long lunches, and everything in between.
            </p>
            <Link className="home-button home-button--dark" to="/shop" onClick={toTop}>
              Shop the pantry
              <span aria-hidden="true">&#8594;</span>
            </Link>
          </div>
          <div className="home-hero__image-wrap">
            <img
              className="home-hero__image"
              src="/main/hero-pantry.jpg"
              srcSet={editorialImageSrcSet("/main/hero-pantry.jpg")}
              sizes="(max-width: 820px) 100vw, 50vw"
              alt="A sunlit table set with pasta, tomatoes, lemons, olives, and olive oil"
              width="2200"
              height="1466"
              loading="eager"
            />
            <p className="home-hero__note" aria-hidden="true">
              Everyday ingredients,
              <br /> anything-but-everyday flavor.
            </p>
          </div>
        </section>

        <section className="home-picks" aria-labelledby="home-picks-title">
          <div className="home-picks__intro">
            <p className="home-kicker">Our picks</p>
            <h2 id="home-picks-title">A very good place to begin</h2>
            <p>
              Two pantry favorites with big personality, chosen by our small
              team for gifting, sharing, and keeping all to yourself.
            </p>
            <Link className="home-text-link" to="/shop?collection=best-sellers" onClick={toTop}>
              See all bestsellers <span aria-hidden="true">&#8594;</span>
            </Link>
          </div>

          {picks.map((product, index) => (
            <div className="home-picks__card" key={product.id}>
              <ProductCard
                product={product}
                tone={index === 0 ? "sun" : "clay"}
                priority={index === 0}
              />
            </div>
          ))}
        </section>

        <section className="home-categories" aria-labelledby="home-categories-title">
          <div className="home-categories__statement">
            <p className="home-kicker">Follow your appetite</p>
            <h2 id="home-categories-title">
              A pantry built to make good food feel close.
            </h2>
          </div>
          <nav className="home-category-rail" aria-label="Shop by food category">
            {siteConfig.categories.map((category, index) => (
              <Link key={category.id} to={category.path} onClick={toTop}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {category.shortName}
              </Link>
            ))}
          </nav>
        </section>

        <div className="home-collection-list">
          {homeCollections.map((collection, sectionIndex) => {
            const products = productData
              .filter((product) => product.category === collection.category)
              .slice(0, 3);
            const headingId = `home-${collection.categoryId}-title`;

            return (
              <section
                className={`home-collection home-collection--${collection.tone}`}
                aria-labelledby={headingId}
                key={collection.categoryId}
              >
                <header className="home-collection__header">
                  <div>
                    <p className="home-kicker">{collection.eyebrow}</p>
                    <h2 id={headingId}>{collection.title}</h2>
                  </div>
                  <div className="home-collection__summary">
                    <p>{collection.description}</p>
                    <Link
                      className="home-text-link"
                      to={`/shop?category=${collection.categoryId}`}
                      onClick={toTop}
                    >
                      Shop {collection.category.toLowerCase()}
                      <span aria-hidden="true">&#8594;</span>
                    </Link>
                  </div>
                </header>

                <div className="home-collection__grid">
                  {products.map((product, productIndex) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      tone={sectionIndex % 2 === 0 ? "paper" : "cream"}
                      priority={sectionIndex === 0 && productIndex === 0}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <section className="home-manifesto" aria-labelledby="home-manifesto-title">
          <p className="home-kicker">Our way of doing things</p>
          <h2 id="home-manifesto-title">
            The best pantry is personal: made by people, full of place, and
            meant to be used.
          </h2>
          <div className="home-manifesto__principles">
            <p><span>01</span> Small makers, thoughtfully chosen</p>
            <p><span>02</span> Honest ingredients, clearly shared</p>
            <p><span>03</span> Everyday food, worth slowing down for</p>
          </div>
          <Link className="home-button home-button--light" to="/about" onClick={toTop}>
            Meet {siteConfig.brandName}
            <span aria-hidden="true">&#8594;</span>
          </Link>
        </section>

        <section className="home-story" aria-labelledby="home-story-title">
          <div className="home-story__image">
            <img
              src="/main/about-kitchen.jpg"
              srcSet={editorialImageSrcSet("/main/about-kitchen.jpg")}
              sizes="(max-width: 820px) 100vw, 50vw"
              alt="Two food makers preparing tomatoes and pantry packages in a sunlit workshop"
              width="1800"
              height="1200"
              loading="lazy"
              decoding="async"
            />
            <span>Notes from the kitchen</span>
          </div>
          <article className="home-story__copy">
            <p className="home-kicker">Pantry journal · Story 01</p>
            <h2 id="home-story-title">Why the simplest pasta can be the most memorable</h2>
            <p>
              Great pasta is less about a long ingredient list and more about
              knowing what each ingredient can do. Start with texture, save a
              cup of cooking water, and let the sauce meet the pasta in the pan.
            </p>
            <Link className="home-text-link" to="/journal" onClick={toTop}>
              Read our kitchen notes <span aria-hidden="true">&#8594;</span>
            </Link>
          </article>
        </section>

        <section
          className="home-editorial-paths"
          aria-label={`More from the ${siteConfig.brandName} table`}
        >
          <article className="home-editorial-path home-editorial-path--recipes">
            <p className="home-kicker">Cook with us</p>
            <h2>Open the pantry. Dinner follows.</h2>
            <p>
              Flexible recipes, useful technique, and simple combinations for
              the ingredients already earning their shelf space.
            </p>
            <Link className="home-text-link" to="/recipes" onClick={toTop}>
              Find a recipe <span aria-hidden="true">&#8594;</span>
            </Link>
          </article>
          <article className="home-editorial-path home-editorial-path--gifts">
            <p className="home-kicker">Give generously</p>
            <h2>A pantry gift feels personal.</h2>
            <p>
              Thoughtful Italian ingredients for hosts, teams, clients, and
              the people whose tables you would like to join.
            </p>
            <Link className="home-text-link" to="/gifts" onClick={toTop}>
              Explore corporate gifts <span aria-hidden="true">&#8594;</span>
            </Link>
          </article>
        </section>
      </main>
    </div>
  );
}

export default Main;
